import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BrandName from '../components/BrandName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    requestType: 'demo',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      toast.success(res.data?.message || "Thank you! We'll be in touch within 24 hours.");
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        requestType: 'demo',
      });
    } catch (err) {
      console.error('Contact submission failed:', err);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6" style={{ background: 'linear-gradient(180deg, #A9C9D9 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8" style={{ color: '#1A1A1A' }}>
              Let's Talk Sustainability
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto">
              Schedule a demo or consultation to see how <BrandName /> can transform your ESG strategy
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <motion.div
              className="lg:col-span-2 space-y-8"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div>
                <h2 className="text-3xl font-bold mb-6" style={{ color: '#1A1A1A' }}>
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Whether you're ready for a demo or just want to learn more about blockchain ESG solutions, our team is here to help.
                </p>
              </div>

              <div className="space-y-6">
                <ContactInfoItem
                  icon={<Mail size={24} style={{ color: '#7CB8D6' }} />}
                  title="Email"
                  content="info.climateco2re@gmail.com"
                />
                <ContactInfoItem
                  icon={<Phone size={24} style={{ color: '#7CB8D6' }} />}
                  title="Phone"
                  content="+92 314 3403 809"
                />
                <ContactInfoItem
                  icon={<MapPin size={24} style={{ color: '#7CB8D6' }} />}
                  title="Headquarters"
                  content="Lahore, Pakistan"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="p-12 rounded-3xl bg-white border-2 border-gray-100">
                <div className="mb-8">
                  <label className="block text-sm font-semibold mb-4" style={{ color: '#1A1A1A' }}>
                    I'm interested in:
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {['demo', 'consultation', 'pricing'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFormData({ ...formData, requestType: type })}
                        className="px-6 py-3 rounded-full font-semibold capitalize transition-all duration-300"
                        style={{
                          background: formData.requestType === type ? '#7CB8D6' : 'transparent',
                          color: formData.requestType === type ? '#FFFFFF' : '#1A1A1A',
                          border: `2px solid ${formData.requestType === type ? '#7CB8D6' : '#E5E7EB'}`,
                        }}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
                      placeholder="+92 314 3403 809"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
                    placeholder="Your Company Name"
                  />
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#1A1A1A' }}>
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-blue-400 transition-colors resize-none"
                    placeholder="Tell us about your ESG goals and challenges..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="btn-premium w-full py-5 rounded-full text-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: '#7CB8D6' }}
                  whileHover={{ scale: submitting ? 1 : 1.02 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                >
                  <span>{submitting ? 'Sending...' : 'Send Message'}</span>
                  {!submitting && <Send size={20} />}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactInfoItem = ({ icon, title, content }) => {
  return (
    <div className="flex items-start gap-4">
      <div className="p-3 rounded-xl" style={{ background: 'rgba(124, 184, 214, 0.1)' }}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold mb-1" style={{ color: '#7CB8D6' }}>
          {title}
        </p>
        <p className="text-gray-700">{content}</p>
      </div>
    </div>
  );
};

export default Contact;